<template>
  <v-dialog :model-value="modelValue" max-width="640" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <div class="pdlg" role="dialog" aria-modal="true" aria-labelledby="add-h">
      <div class="pdlg-head">
        <h2 id="add-h">{{ mode === 'name' ? 'Add provider without an NPI' : 'Add provider' }}</h2>
        <button type="button" class="pdlg-x" aria-label="Close" @click="close">&#10005;</button>
      </div>

      <div v-if="mode === 'search'" class="pdlg-body">
        <NpiSearchField :review-entity-id="reviewEntityId" @pick="onPick" />
        <button type="button" class="pdlg-link" @click="switchMode('name')">Can't find them? Add without an NPI</button>
        <RegistryCard v-if="picked" :result="picked">
          The NPI Registry lists {{ picked.locations.length }} practice location{{ picked.locations.length === 1 ? '' : 's' }} for this provider. We use the NPI to keep one profile for this provider across all of your audits.
        </RegistryCard>
        <LockedHospitalRow :hospital="hospital" :service-line="serviceLine" />
        <v-alert v-if="error" type="error" variant="tonal" density="compact">
          {{ error }}
          <a v-if="limitReached" href="#" class="pdlg-upgrade" @click.prevent="upgrade">Upgrade to add more &rarr;</a>
        </v-alert>
      </div>

      <div v-else class="pdlg-body">
        <div class="pdlg-warn">
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8a5a00" stroke-width="2" stroke-linecap="round"><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17h.01" /></svg>
          <span>Without an NPI, we can't match this provider across hospitals or locations, and we'll match AI answers by name only. You can add the NPI later from the provider's page.</span>
        </div>
        <div class="pdlg-names">
          <div class="pdlg-field"><label for="fn">First name</label><input id="fn" v-model="firstName" autocomplete="off" /></div>
          <div class="pdlg-field"><label for="ln">Last name</label><input id="ln" v-model="lastName" autocomplete="off" /></div>
        </div>
        <LockedHospitalRow :hospital="hospital" :service-line="serviceLine" />
        <v-alert v-if="error" type="error" variant="tonal" density="compact">
          {{ error }}
          <a v-if="limitReached" href="#" class="pdlg-upgrade" @click.prevent="upgrade">Upgrade to add more &rarr;</a>
        </v-alert>
      </div>

      <div class="pdlg-foot" :class="{ split: mode === 'name' }">
        <button v-if="mode === 'name'" type="button" class="pdlg-link lg" @click="switchMode('search')">&larr; Back to NPI search</button>
        <div class="pdlg-btns">
          <button type="button" class="pdlg-btn" @click="close">Cancel</button>
          <button type="button" class="pdlg-btn primary" :disabled="!canSubmit || saving" @click="submit">
            {{ saving ? 'Adding…' : 'Add provider' }}
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import NpiSearchField from './NpiSearchField.vue'
import RegistryCard from './RegistryCard.vue'
import LockedHospitalRow from './LockedHospitalRow.vue'
import { addProvider } from './providersApi'
import { track } from './track'
import { errorCode, errorMessage } from './providerErrors'

const LIMIT_MESSAGE = 'Your plan includes 1 provider per audit.'

export default {
  name: 'AddProviderDialog',
  components: { NpiSearchField, RegistryCard, LockedHospitalRow },
  props: {
    modelValue: { type: Boolean, default: false },
    reviewEntityId: { type: [Number, String], default: null },
    hospital: { type: String, default: '' },
    serviceLine: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'added'],
  data() {
    return { mode: 'search', picked: null, firstName: '', lastName: '', saving: false, error: '', limitReached: false }
  },
  computed: {
    canSubmit() {
      return this.mode === 'search' ? !!this.picked : !!(this.firstName.trim() && this.lastName.trim())
    }
  },
  watch: {
    modelValue(open) {
      if (open) Object.assign(this, { mode: 'search', picked: null, firstName: '', lastName: '', saving: false, error: '', limitReached: false })
    }
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    onPick(r) {
      this.picked = r
      this.error = ''
      this.limitReached = false
    },
    switchMode(mode) {
      this.mode = mode
      this.picked = null
      this.error = ''
      this.limitReached = false
    },
    upgrade() {
      this.close()
      this.$router.push('/profile?tab=billing')
    },
    async submit() {
      if (!this.canSubmit || this.saving) return
      const byNpi = this.mode === 'search'
      this.saving = true
      this.error = ''
      this.limitReached = false
      try {
        const body = byNpi ? { npi: this.picked.npi } : { first_name: this.firstName.trim(), last_name: this.lastName.trim() }
        const res = await addProvider(this.reviewEntityId, body)
        track('provider_added', { method: byNpi ? 'npi' : 'name' })
        this.$emit('added', res.provider)
        this.close()
      } catch (e) {
        if (errorCode(e) === 'provider_limit') {
          this.limitReached = true
          this.error = LIMIT_MESSAGE
        } else {
          this.error = errorMessage(e, "We couldn't add this provider. Try again.")
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped src="./dialogStyles.css"></style>
<style scoped>
.pdlg { background: #fff; border-radius: 14px; display: flex; flex-direction: column; max-height: calc(100vh - 48px); overflow-y: auto; box-shadow: 0 16px 48px rgba(13, 30, 53, 0.25); }
.pdlg-warn { display: flex; gap: 10px; align-items: flex-start; font-size: 14px; color: #5a3d00; background: #fef3d9; border: 1px solid #f3dca4; border-radius: 10px; padding: 12px 14px; }
.pdlg-warn svg { flex: 0 0 auto; margin-top: 1px; }
.pdlg-upgrade { margin-left: 8px; font-weight: 600; color: inherit; text-decoration: underline; }
.pdlg-names { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.pdlg-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.pdlg-field label { font-size: 13px; font-weight: 600; }
.pdlg-field input { height: 48px; padding: 0 12px; border: 1px solid #cfc8b6; border-radius: 10px; font: inherit; color: #0d1e35; background: #fff; box-sizing: border-box; width: 100%; }
</style>
